import { useEffect, useRef, useState } from 'react'
import type { Roll } from '@shared/types'
import { DiceError, parseFormula } from '@shared/rules/dice'
import { ApiError, api } from '@/lib/api'
import { Button, ErrorNote, Input } from '@/components/ui'
import { RollResultCard } from './RollResultCard'
import { cn } from '@/lib/cn'

const QUICK_DICE = [4, 6, 8, 10, 12, 20, 100]

export interface DiceRollerProps {
  campaignId: string
  /** Attributes the roll to a character in the feed. */
  characterId?: string | null
  canRollPrivately?: boolean
  onRolled?: (roll: Roll) => void
}

/**
 * A floating roller available anywhere inside a campaign. The formula is
 * validated locally for instant feedback, but the dice themselves are rolled on
 * the server.
 */
export function DiceRoller({ campaignId, characterId = null, canRollPrivately = false, onRolled }: DiceRollerProps) {
  const [open, setOpen] = useState(false)
  const [formula, setFormula] = useState('1d20')
  const [context, setContext] = useState('')
  const [advantage, setAdvantage] = useState<'none' | 'advantage' | 'disadvantage'>('none')
  const [hidden, setHidden] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [last, setLast] = useState<Roll | null>(null)
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Local parse gives immediate feedback without a round trip.
  let preview: string | null = null
  let parseError: string | null = null
  try {
    preview = parseFormula(formula).normalised
  } catch (err) {
    parseError = err instanceof DiceError ? err.message : 'Not a dice formula'
  }

  const d20Only = /^\s*1?d20/i.test(formula.trim())

  async function roll(override?: string) {
    const text = override ?? formula
    setBusy(true)
    setError(null)
    try {
      const res = await api.createRoll(campaignId, {
        formula: text,
        context: context.trim(),
        characterId,
        advantage: override ? 'none' : advantage,
        private: hidden,
      })
      setLast(res.roll)
      onRolled?.(res.roll)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not roll')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={open ? 'Close the dice roller' : 'Open the dice roller'}
        className={cn(
          'pressable fixed bottom-5 right-5 z-40 grid size-14 place-items-center rounded-full',
          'shadow-[0_8px_32px_-8px_rgb(0_0_0/0.8)]',
          open
            ? 'bg-gold-500 text-on-accent'
            : 'material text-gold-400 hover:text-gold-500',
        )}
      >
        <span className="font-display text-[15px] font-bold">{open ? '✕' : '20'}</span>
      </button>

      {open && (
        <div
          ref={panel}
          role="dialog"
          aria-label="Dice roller"
          className="material panel-lit fixed bottom-24 right-5 z-40 w-[min(22rem,calc(100vw-2.5rem))] space-y-3 rounded-[--radius-card] p-4"
        >
          <div className="flex flex-wrap gap-1.5">
            {QUICK_DICE.map((sides) => (
              <button
                key={sides}
                type="button"
                disabled={busy}
                onClick={() => { setFormula(`1d${sides}`); void roll(`1d${sides}`) }}
                className="rounded-lg border border-ink-600 bg-ink-800 px-2.5 py-1.5 font-mono text-xs text-ink-200 transition-colors hover:border-gold-600 hover:text-gold-400 disabled:opacity-50"
              >
                d{sides}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <Input
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !parseError) void roll() }}
              aria-label="Dice formula"
              placeholder="2d6+3"
              className={cn('font-mono', parseError && formula.trim() && 'border-blood-500/60')}
            />
            <Input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !parseError) void roll() }}
              aria-label="What is this roll for?"
              placeholder="What for? (Stealth check…)"
              maxLength={120}
            />
          </div>

          {d20Only && (
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-ink-900/70 p-1">
              {([
                ['disadvantage', 'Disadv.'],
                ['none', 'Normal'],
                ['advantage', 'Advantage'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAdvantage(value)}
                  className={cn(
                    'rounded-lg py-1.5 text-[12px] font-medium transition-colors',
                    advantage === value ? 'bg-ink-750 text-gold-400' : 'text-ink-400 hover:text-ink-200',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {canRollPrivately && (
            <label className="flex items-center gap-2 text-[13px] text-ink-300">
              <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)}
                className="size-4 accent-arcane-500" />
              Hide this roll from players
            </label>
          )}

          {parseError && formula.trim() ? (
            <p className="text-xs text-blood-500">{parseError}</p>
          ) : (
            <p className="font-mono text-xs text-ink-500">{preview}</p>
          )}

          {error && <ErrorNote>{error}</ErrorNote>}

          <Button variant="primary" className="w-full" busy={busy} disabled={Boolean(parseError)}
            onClick={() => void roll()}>
            Roll
          </Button>

          {last && <RollResultCard roll={last} showWho={false} />}
        </div>
      )}
    </>
  )
}
